/**
 * Providers scoped por ruta para evitar inicialización global innecesaria.
 * Cada wrapper provee solo los contextos que la página/sección necesita.
 */
import React from "react";
import PropTypes from "prop-types";

// Providers pesados (sockets, APIs externas, etc.)
import { ChatProvider } from "../context/ChatContext";
import { WsspAuthProvider } from "../context/WsspAuthContext";
import { WhatsAppSocketProvider } from "../context/WhatsAppSocketContext";
import { WhatsAppProvider } from "../context/WhatsAppContext";
import { TrackingSocketProvider } from "../context/TrackingSocketContext";
import { TrackingProvidersProvider } from "../context/TrackingProvidersContext";
import { GoogleMapsProvider } from "../context/GoogleMapsContext";
import { HelpProvider } from "../context/HelpContext";
import { TicketsProvider } from "../context/TicketsContext";
import { VehicleFilterProvider } from "../context/VehicleFilterContext";
import { PersonsFilterProvider } from "../context/PersonsFilterContext";
import { TrackingViewsProvider } from "../context/TrackingViewsContext";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { ReCaptchaProvider } from "../context/ReCaptchaContext";

// =====================================================
// Wrapper para paginas de mensajeria/chat interno
// Rutas: /message
// =====================================================
export const ChatProviders = ({ children }) => (
  <ChatProvider>
    {children}
  </ChatProvider>
);
ChatProviders.propTypes = { children: PropTypes.node.isRequired };

// =====================================================
// Wrapper para WhatsApp (incluye socket general + wssp)
// Rutas: /wssp-connection
// =====================================================
export const WhatsAppProviders = ({ children }) => (
  <WsspAuthProvider>
    <WhatsAppSocketProvider>
      <WhatsAppProvider>
        {children}
      </WhatsAppProvider>
    </WhatsAppSocketProvider>
  </WsspAuthProvider>
);
WhatsAppProviders.propTypes = { children: PropTypes.node.isRequired };

// =====================================================
// Wrapper para tracking vehicular
// Rutas: /vehicle-tracking, /multi-tracking
// =====================================================
export const TrackingProviders = ({ children }) => (
  <TrackingSocketProvider>
    <TrackingProvidersProvider>
      {children}
    </TrackingProvidersProvider>
  </TrackingSocketProvider>
);
TrackingProviders.propTypes = { children: PropTypes.node.isRequired };

// =====================================================
// Wrapper para multi-tracking (tracking + views + filters)
// Rutas: /multi-tracking
// =====================================================
export const MultiTrackingProviders = ({ children }) => (
  <VehicleFilterProvider>
    <TrackingViewsProvider>
      <TrackingSocketProvider>
        <TrackingProvidersProvider>
          {children}
        </TrackingProvidersProvider>
      </TrackingSocketProvider>
    </TrackingViewsProvider>
  </VehicleFilterProvider>
);
MultiTrackingProviders.propTypes = { children: PropTypes.node.isRequired };

// =====================================================
// Wrapper para páginas con Google Maps
// Rutas: /geofences-travel-and-more, /vehicle-tracking, etc.
// =====================================================
export const MapsProviders = ({ children }) => (
  <GoogleMapsProvider>
    {children}
  </GoogleMapsProvider>
);
MapsProviders.propTypes = { children: PropTypes.node.isRequired };

// =====================================================
// Wrapper para help/tickets
// Rutas: /help/*
// =====================================================
export const HelpProviders = ({ children }) => (
  <HelpProvider>
    <TicketsProvider>
      {children}
    </TicketsProvider>
  </HelpProvider>
);
HelpProviders.propTypes = { children: PropTypes.node.isRequired };

// =====================================================
// Wrapper para filtros de vehículos
// Rutas: /alerts-vehicles, dashboard vehicle
// =====================================================
export const VehicleFilterProviders = ({ children }) => (
  <VehicleFilterProvider>
    {children}
  </VehicleFilterProvider>
);
VehicleFilterProviders.propTypes = { children: PropTypes.node.isRequired };

// =====================================================
// Wrapper para ReCaptcha (login, register, recovery)
// =====================================================
export const RecaptchaProviders = ({ children }) => (
  <GoogleReCaptchaProvider
    reCaptchaKey={process.env.REACT_APP_GOOGLE_RECAPTCHA_API_KEY}
    scriptProps={{
      async: true,
      defer: true,
      appendTo: "head",
    }}
    language="es"
    useRecaptchaNet={true}
  >
    <ReCaptchaProvider>
      {children}
    </ReCaptchaProvider>
  </GoogleReCaptchaProvider>
);
RecaptchaProviders.propTypes = { children: PropTypes.node.isRequired };

// =====================================================
// Wrapper para transport calculator (necesita maps)
// Rutas: /transport-calculator/*
// =====================================================
export const TransportProviders = ({ children }) => (
  <GoogleMapsProvider>
    {children}
  </GoogleMapsProvider>
);
TransportProviders.propTypes = { children: PropTypes.node.isRequired };

// =====================================================
// Wrapper para dashboards de monitoreo (necesitan tracking + maps)
// Rutas: /dashboard (roles monitor)
// =====================================================
export const DashboardMonitorProviders = ({ children }) => (
  <TrackingSocketProvider>
    <TrackingProvidersProvider>
      <VehicleFilterProvider>
        <GoogleMapsProvider>
          {children}
        </GoogleMapsProvider>
      </VehicleFilterProvider>
    </TrackingProvidersProvider>
  </TrackingSocketProvider>
);
DashboardMonitorProviders.propTypes = { children: PropTypes.node.isRequired };

// =====================================================
// Wrapper para operaciones (scheduler usa AddVehicleModal → useTrackingProviders)
// Rutas: /scheduler/*
// =====================================================
export const OperationsProviders = ({ children }) => (
  <TrackingSocketProvider>
    <TrackingProvidersProvider>
      {children}
    </TrackingProvidersProvider>
  </TrackingSocketProvider>
);
OperationsProviders.propTypes = { children: PropTypes.node.isRequired };

// =====================================================
// Wrapper para vehículos (filtros + tracking settings)
// Rutas: /vehicles/*
// =====================================================
export const VehiclesProviders = ({ children }) => (
  <VehicleFilterProvider>
    <TrackingSocketProvider>
      <TrackingProvidersProvider>
        {children}
      </TrackingProvidersProvider>
    </TrackingSocketProvider>
  </VehicleFilterProvider>
);
VehiclesProviders.propTypes = { children: PropTypes.node.isRequired };

// =====================================================
// Wrapper para preoperacional (necesita tracking para TrackingCell)
// Rutas: /preoperacional, /preoperacional/config
// =====================================================
export const PreoperacionalProviders = ({ children }) => (
  <TrackingSocketProvider>
    <TrackingProvidersProvider>
      {children}
    </TrackingProvidersProvider>
  </TrackingSocketProvider>
);
PreoperacionalProviders.propTypes = { children: PropTypes.node.isRequired };

// =====================================================
// Wrapper para filtros de personas (human resources)
// Rutas: /alerts-persons
// =====================================================
export const PersonsFilterProviders = ({ children }) => (
  <PersonsFilterProvider>
    {children}
  </PersonsFilterProvider>
);
PersonsFilterProviders.propTypes = { children: PropTypes.node.isRequired };
